import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { approveManualOrder, mapManualApprovalError, useTranslation } from '@dukanti/shared';
import type { ManualApprovalTransitionResult } from '@dukanti/shared';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

export interface UseApproveManualOrderOptions {
  readonly orderId: string;
  readonly storeId: string | null;
}

export function useApproveManualOrder(
  opts: UseApproveManualOrderOptions,
): UseMutationResult<ManualApprovalTransitionResult, unknown, void> {
  const { orderId, storeId } = opts;
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation<ManualApprovalTransitionResult, unknown, void>({
    mutationFn: () => approveManualOrder(supabase, orderId),
    onSuccess: (result) => {
      if (!result.success) {
        toast.error(t(mapManualApprovalError(undefined, result, 'approve')));
        return;
      }
      if (result.order) {
        queryClient.setQueryData(
          ['admin-order-detail', orderId],
          (old: { order: unknown; items: unknown; events: unknown } | undefined) =>
            old ? { ...old, order: result.order } : old,
        );
      }
      if (storeId) {
        void queryClient.invalidateQueries({ queryKey: ['store-orders', storeId] });
      }
      void queryClient.invalidateQueries({ queryKey: ['incoming-orders-count-user'] });
      toast.success(t('admin.orders.manualApproval.approveSuccess' as never));
    },
    onError: (err) => {
      toast.error(t(mapManualApprovalError(err, undefined, 'approve')));
    },
  });
}
